/**
 * Client-side PDF generator using jsPDF + html2canvas.
 * Strategy:
 *   - Render the "ReportTemplate" React component into an OFF-SCREEN DOM node
 *   - html2canvas captures each logical section at 2x scale
 *   - jsPDF appends each section image onto its own A4 portrait page
 * This keeps text crisp, preserves charts/layouts, and avoids the
 * "one giant tile" pitfall that cripples long documents.
 */
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { saveAs } from "file-saver";

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const MARGIN_MM = 10;

async function captureSectionToCanvas(node, scale = 2) {
  // Ensure fonts are fully loaded before capture
  if (document.fonts && document.fonts.ready) {
    try {
      await document.fonts.ready;
    } catch {
      // ignore
    }
  }
  return html2canvas(node, {
    scale,
    backgroundColor: "#FFFFFF",
    useCORS: true,
    allowTaint: false,
    logging: false,
    imageTimeout: 15000,
  });
}

async function addCanvasAsPage(pdf, canvas, first = false) {
  if (!first) pdf.addPage();
  const usableW = A4_WIDTH_MM - 2 * MARGIN_MM;
  const usableH = A4_HEIGHT_MM - 2 * MARGIN_MM;
  const imgW = usableW;
  const imgH = (canvas.height / canvas.width) * imgW;
  const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
  if (imgH <= usableH) {
    pdf.addImage(dataUrl, "JPEG", MARGIN_MM, MARGIN_MM, imgW, imgH);
    return;
  }
  // If the section is taller than one page, tile it vertically.
  const pageCanvas = document.createElement("canvas");
  const pageCtx = pageCanvas.getContext("2d");
  const tileHeightPx = Math.floor((usableH / imgW) * canvas.width);
  pageCanvas.width = canvas.width;
  pageCanvas.height = tileHeightPx;
  let yOffset = 0;
  let pageIdx = 0;
  while (yOffset < canvas.height) {
    const slice = Math.min(tileHeightPx, canvas.height - yOffset);
    pageCanvas.height = slice;
    pageCtx.fillStyle = "#FFFFFF";
    pageCtx.fillRect(0, 0, canvas.width, slice);
    pageCtx.drawImage(
      canvas,
      0,
      yOffset,
      canvas.width,
      slice,
      0,
      0,
      canvas.width,
      slice
    );
    const pageData = pageCanvas.toDataURL("image/jpeg", 0.92);
    if (pageIdx > 0) pdf.addPage();
    const pageImgH = (slice / canvas.width) * imgW;
    pdf.addImage(pageData, "JPEG", MARGIN_MM, MARGIN_MM, imgW, pageImgH);
    yOffset += slice;
    pageIdx += 1;
  }
}

/**
 * Captures the sections of the offscreen template (must already be mounted).
 * Each section is a child element with [data-report-section].
 */
export async function generatePdf({ rootNode, filename }) {
  if (!rootNode) throw new Error("Le template de rapport est introuvable.");
  const sections = Array.from(
    rootNode.querySelectorAll("[data-report-section]")
  );
  if (!sections.length) {
    throw new Error("Le template ne contient aucune section exportable.");
  }
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });
  pdf.setProperties({
    title: filename,
    subject: "Rapport d'Analyse Vitaliste des Finances Publiques",
    creator: "VITAE-PUBLICA",
    author: "VITAE-PUBLICA",
    keywords: "VITAE-PUBLICA, vitalist, finances publiques, pédagogique",
  });
  for (let i = 0; i < sections.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const canvas = await captureSectionToCanvas(sections[i], 2);
    // eslint-disable-next-line no-await-in-loop
    await addCanvasAsPage(pdf, canvas, i === 0);
  }
  const blob = pdf.output("blob");
  saveAs(blob, filename);
  return blob.size;
}

/**
 * Generates a single high-resolution A3-landscape PNG poster.
 * Uses a ~300 DPI approximation: A3 landscape at 300 DPI = 4961 × 3508 px.
 * We render the offscreen poster node at scale=3 and downscale only if the
 * source is smaller than target.
 */
export async function generatePosterPng({ posterNode, filename }) {
  if (!posterNode) throw new Error("Poster template introuvable.");
  // eslint-disable-next-line no-console
  console.log(
    "[report] generatePosterPng node dims",
    posterNode.offsetWidth,
    "x",
    posterNode.offsetHeight
  );
  // scale=2 on a 1754x1240 base = ~3508x2480 px which is already
  // >= A3@200DPI and prints cleanly at A3. scale=3 triggers ~80 MP
  // canvas which OOMs in headless browsers.
  const canvas = await captureSectionToCanvas(posterNode, 2);
  const blob = await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/png", 1)
  );
  if (!blob) throw new Error("Conversion PNG impossible (canvas vide).");
  saveAs(blob, filename);
  return blob.size;
}
